import React, { useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { EmployeeManagementTab, TabNavigationProps } from '../../types/employee';
import { calculateBusinessScale } from '../../utils/businessScale';

const TabNavigation: React.FC<TabNavigationProps> = ({
  activeTab,
  onTabChange,
  employees = [],
  selectedMonth,
  accountNumber = 1,
  isBusinessScale5Plus,
}) => {
  const scaleLabel = useMemo(() => {
    if (isBusinessScale5Plus != null) {
      return isBusinessScale5Plus ? '5인 이상' : '5인 미만';
    }
    if (!employees.length || selectedMonth == null) return null;
    const is5Plus = calculateBusinessScale(employees, selectedMonth, accountNumber);
    return is5Plus ? '5인 이상' : '5인 미만';
  }, [employees, selectedMonth, accountNumber, isBusinessScale5Plus]);

  const tabs: Array<{ id: EmployeeManagementTab; label: string; description: string }> = [
    {
      id: 'calendar' as const,
      label: '캘린더',
      description: '근무 일정 관리'
    },
    {
      id: 'payroll' as const,
      label: '급여관리',
      description: '급여 계산 및 관리'
    },
    {
      id: 'employees' as const,
      label: '직원관리',
      description: '직원 정보 관리'
    },
    {
      id: 'records' as const,
      label: '인사기록',
      description: '근로자명부·출근부·임금대장·임금명세서 PDF'
    }
  ];

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      mb: 2,
      p: 1,
      background: 'var(--bg-base)',
      borderRadius: 1,
      border: '1px solid var(--border-subtle)',
    }}>
      {scaleLabel != null && (
        <Typography variant="body2" color="text.secondary" sx={{ mr: 1, fontWeight: 500 }}>
          {scaleLabel}
        </Typography>
      )}
      {tabs.map(tab => (
        <Button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 1.5,
            px: 2,
            borderRadius: 1,
            background: activeTab === tab.id
              ? 'var(--primary)'
              : 'transparent',
            color: activeTab === tab.id ? 'var(--fg-on-primary)' : 'var(--fg-secondary)',
            transition: 'all 0.2s ease',
            '&:hover': {
              background: activeTab === tab.id ? 'var(--primary-hover)' : 'var(--bg-elev-1)',
            },
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: activeTab === tab.id ? 600 : 500,
              fontSize: '0.9rem',
              textTransform: 'none',
            }}
          >
            {tab.label}
          </Typography>
        </Button>
      ))}
    </Box>
  );
};

export default TabNavigation;
